import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { Download, X } from 'lucide-react'
import { decodeUploadFileName } from '@numeo/types-core'
import { Button, Spinner } from '@/components/ds2'
import { PDF_PREVIEW_DEFAULT_ZOOM } from '@/components/shared/document-viewer/previewZoom'
import { DocumentImageStage } from '@/components/shared/document-viewer/DocumentImageStage'
import { PdfPreviewToolbar } from '@/pages/dashboard/pages/shared-components/PdfPreviewToolbar'
import { rotateBy } from '@/components/shared/document-viewer/documentViewTransform'
import { downloadFromUrl } from '@/pages/dashboard/pages/accounting/downloadHelpers'
import { trackBoxTruckUiInteraction } from '../utils/analytics'

const PdfDocumentPreview = lazy(() =>
    import('@/pages/dashboard/pages/shared-components/PdfDocumentPreview').then((module) => ({ default: module.PdfDocumentPreview }))
)

const VIEWPORT_SIZE = 'h-[80vh] w-[calc(100vw-3rem)] max-w-full sm:w-[70vw]'

type DownloadSurface = 'toolbar' | 'fallback'

/** For what the floating toolbar cannot sit on: a video's own controls, or an image that failed and has nothing left to zoom. */
const DownloadButton = ({ onDownload }: { onDownload: () => void }) => (
    <Button type="button" variant="secondary" size="sm" prefix={<Download className="h-4 w-4" />} onClick={onDownload}>
        Download
    </Button>
)

/** An image or PDF with the zoom + rotate + download toolbar. The view is not saved: the lightbox keys this by blob URL, so
 *  every document opens at 100% and upright. */
const ZoomableDocument = ({
    blobUrl,
    fileName,
    isPdf,
    onDownload,
}: {
    blobUrl: string
    fileName: string
    isPdf: boolean
    onDownload: (surface: DownloadSurface) => void
}) => {
    const [zoom, setZoom] = useState(PDF_PREVIEW_DEFAULT_ZOOM)
    const [rotation, setRotation] = useState(0)
    const [failed, setFailed] = useState(false)

    const fail = (kind: 'image' | 'pdf') => {
        setFailed(true)
        trackBoxTruckUiInteraction('document_preview_failed', kind)
    }

    // A PDF that pdf.js cannot render still opens in the browser's own viewer, which has its own controls.
    if (failed && isPdf) return <iframe src={blobUrl} title={fileName} className={`${VIEWPORT_SIZE} border-0`} />
    // An image the browser cannot decode has nothing to zoom or rotate, so the toolbar goes with it. The file can still be saved and opened.
    if (failed) {
        return (
            <div className="flex flex-col items-center gap-3 px-6 py-10">
                <p role="alert" className="text-sm text-danger">
                    Could not display this file. Download it to view it on your device.
                </p>
                <DownloadButton onDownload={() => onDownload('fallback')} />
            </div>
        )
    }

    return (
        <div className={`relative overflow-hidden bg-surface-secondary ${VIEWPORT_SIZE}`}>
            {isPdf ? (
                <Suspense
                    fallback={
                        <div className="flex h-full items-center justify-center">
                            <Spinner color="accent" size={24} />
                        </div>
                    }
                >
                    <PdfDocumentPreview fileUrl={blobUrl} title={fileName} zoom={zoom} rotation={rotation} onError={() => fail('pdf')} />
                </Suspense>
            ) : (
                <DocumentImageStage src={blobUrl} alt={fileName} zoom={zoom} rotation={rotation} onZoomChange={setZoom} onError={() => fail('image')} />
            )}
            <PdfPreviewToolbar
                zoom={zoom}
                onZoomChange={setZoom}
                onZoomReset={() => setZoom(PDF_PREVIEW_DEFAULT_ZOOM)}
                onRotate={(delta) => setRotation((current) => rotateBy(current, delta))}
                onDownload={() => onDownload('toolbar')}
            />
        </div>
    )
}

/** In-place document preview — a fixed overlay (no new tab, no route change) showing the fetched blob:
 *  images and PDFs with zoom / pan / rotate, videos inline. Backdrop click / ✕ / Escape close it. The caller owns
 *  the blob URL lifecycle (fetch on open, revoke on close). */
export const DocumentLightbox = ({
    fileName: storedFileName,
    contentType,
    blobUrl,
    onClose,
}: {
    fileName: string
    contentType?: string
    /** Object URL of the fetched bytes; null while still loading. */
    blobUrl: string | null
    onClose: () => void
}) => {
    // A pan that starts on the document and is released over the backdrop must not close the preview.
    const pressStartedOnBackdrop = useRef(false)

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [onClose])

    // A name stored before uploads decoded it is garbled; show it as uploaded, like its chip (DocumentFileName).
    const fileName = decodeUploadFileName(storedFileName)
    const lowerFileName = fileName.toLowerCase()
    const isPdf = contentType === 'application/pdf' || lowerFileName.endsWith('.pdf')
    const isVideo = contentType?.startsWith('video/') || ['.mp4', '.mov', '.webm'].some((extension) => lowerFileName.endsWith(extension))
    const download = (surface: DownloadSurface) => {
        if (!blobUrl) return
        downloadFromUrl(blobUrl, fileName)
        trackBoxTruckUiInteraction('document_downloaded', isVideo ? 'video' : isPdf ? 'pdf' : 'image', { surface })
    }

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-6"
            onMouseDown={(e) => {
                pressStartedOnBackdrop.current = e.target === e.currentTarget
            }}
            onClick={() => {
                if (pressStartedOnBackdrop.current) onClose()
            }}
            role="dialog"
            aria-label={fileName}
        >
            <div className="relative flex max-h-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-surface shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5">
                    <span className="truncate text-sm font-medium text-foreground">{fileName}</span>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close preview"
                        className="rounded-md p-1 text-foreground-muted transition-colors hover:bg-default hover:text-foreground"
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className="flex min-h-[200px] items-center justify-center overflow-auto bg-black/5">
                    {!blobUrl ? (
                        <Spinner color="accent" size={24} />
                    ) : isVideo ? (
                        <div className="flex flex-col items-center gap-3 p-3">
                            <video src={blobUrl} controls preload="metadata" className="max-h-[72vh] max-w-full" />
                            <DownloadButton onDownload={() => download('fallback')} />
                        </div>
                    ) : (
                        <ZoomableDocument key={blobUrl} blobUrl={blobUrl} fileName={fileName} isPdf={isPdf} onDownload={download} />
                    )}
                </div>
            </div>
        </div>
    )
}
